import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { getRedirectPath, ROLE_PERMISSIONS } from './rbac';
import { UserRole as RbacUserRole } from '@/types';
import { useRoleChangeDetection } from '../hooks/useRoleChangeDetection';

// Tipos do usuário
export type UserRole = 'PRODUCAO' | 'AGIS' | 'FABRICANTE' | 'REVENDA';

export interface User {
  id: string;
  nome: string;
  email: string;
  papel: UserRole;
  participantId?: string;
  eventId?: string;
  createdAt: string;
  updatedAt: string;
}

// Funções de cache local para otimizar carregamento
const USER_PROFILE_CACHE_KEY = 'user_profile_cache';
const CACHE_EXPIRY_DAYS = 30; // 30 dias de cache

const saveUserProfileToCache = (user: User) => {
  try {
    localStorage.setItem(USER_PROFILE_CACHE_KEY, JSON.stringify({
      ...user,
      cachedAt: new Date().toISOString()
    }));
    console.log('💾 Perfil salvo no cache local');
  } catch (error) {
    console.warn('Erro ao salvar perfil no cache:', error);
  }
};

const loadUserProfileFromCache = (): User | null => {
  try {
    const cached = localStorage.getItem(USER_PROFILE_CACHE_KEY);
    if (!cached) return null;
    
    const parsed = JSON.parse(cached);
    // Verificar se o cache não é muito antigo (30 dias)
    const cacheAge = new Date().getTime() - new Date(parsed.cachedAt).getTime();
    const maxAge = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000; // 30 dias em ms
    
    if (cacheAge > maxAge) {
      console.log('🗑️ Cache expirado, removendo...');
      localStorage.removeItem(USER_PROFILE_CACHE_KEY);
      return null;
    }
    
    console.log('📋 Perfil carregado do cache local');
    return parsed;
  } catch (error) {
    console.warn('Erro ao carregar perfil do cache:', error);
    return null;
  }
};

const clearUserProfileCache = () => {
  try {
    localStorage.removeItem(USER_PROFILE_CACHE_KEY);
    console.log('🧹 Cache do perfil limpo');
  } catch (error) {
    console.warn('Erro ao limpar cache:', error);
  }
};

// FABRICANTE e REVENDA veem somente a agenda do evento ativo (events.ativo). O vínculo
// vem do e-mail na lista de representantes do participante desse evento, não de
// users.participant_id, que guarda um único vínculo e costuma apontar para uma edição
// anterior. Sem participante no evento ativo (ou em falha) o vínculo fica vazio e a
// Minha Agenda aparece vazia, nunca com a agenda de outro evento.
const resolverVinculoParticipante = async (usuario: User): Promise<User> => {
  if (usuario.papel !== 'FABRICANTE' && usuario.papel !== 'REVENDA') return usuario;

  const semVinculo: User = { ...usuario, participantId: undefined, eventId: undefined };

  try {
    // Compara o e-mail no cliente: o JSONB guarda o e-mail como foi digitado no cadastro
    // do participante, às vezes com maiúsculas ou espaços, e o filtro "cs" é exato.
    const { data, error } = await supabase
      .from('participants')
      .select('id, event_id, representante, events!inner(ativo)')
      .eq('tipo', usuario.papel)
      .eq('events.ativo', true);

    if (error) {
      console.warn('⚠️ [Auth] Não foi possível resolver o participante do evento ativo:', error);
      return semVinculo;
    }

    const normalizar = (email?: string) => (email || '').trim().toLowerCase();
    const emailUsuario = normalizar(usuario.email);
    const participante = (data as Array<{ id: string; event_id: string; representante: Array<{ email?: string }> | null }> | null)
      ?.find(p => (p.representante || []).some(r => normalizar(r.email) === emailUsuario));
    if (!participante) return semVinculo;

    return { ...usuario, participantId: participante.id, eventId: participante.event_id };
  } catch (error) {
    console.warn('⚠️ [Auth] Não foi possível resolver o participante do evento ativo:', error);
    return semVinculo;
  }
};

// Contexto de autenticação
interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  login: (email: string, password: string) => Promise<boolean>; // Compatibilidade
  logout: () => void; // Compatibilidade
  isAuthenticated: boolean; // Compatibilidade
  hasPermission: (action: string, resource?: string) => boolean; // Compatibilidade
  loginWithGoogle?: () => Promise<boolean>; // OAuth
  loginWithMicrosoft?: () => Promise<boolean>; // OAuth
  loginWithLinkedIn?: () => Promise<boolean>; // OAuth
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook para usar o contexto
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de SupabaseAuthProvider');
  }
  return context;
};

// Provedor de autenticação
export function SupabaseAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  // 🔍 FASE 1: Hook de detecção de mudanças de papel para auditoria
  useRoleChangeDetection(user, {
    onRoleChange: (event) => {
      // Log crítico para o problema PRODUCAO -> REVENDA
      if (event.previousRole === 'PRODUCAO' && event.newRole === 'REVENDA') {
        console.error('🚨 [SUPABASE-AUTH] PROBLEMA CRÍTICO DETECTADO!', {
          ...event,
          location: 'SupabaseAuthProvider',
          currentStack: new Error().stack
        });
        
        // Tentar recuperar papel correto do servidor
        // Implementaremos validação cruzada na Fase 3
      }
    },
    logToConsole: true,
    saveToStorage: true
  });

  useEffect(() => {
    // Timeout para evitar loading infinito - reduzido para 5 segundos
    const loadingTimeout = setTimeout(() => {
      if (loading) {
        console.warn('⚠️  Loading timeout (5s) - forçando saída do loading');
        console.warn('⚠️  Estado atual: user =', user, ', session =', session);
        setLoading(false);
      }
    }, 5000); // 5 segundos

    // Renderizar a partir do cache imediatamente; a sessão é conferida logo abaixo
    const cachedProfile = loadUserProfileFromCache();
    if (cachedProfile) {
      console.log('📋 Perfil carregado do cache local:', cachedProfile);
      setUser(cachedProfile);
      setLoading(false);
    }

    // Obter sessão inicial
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('📋 Sessão inicial:', { session: session?.user?.email, error });
      setSession(session);
      if (session?.user) {
        if (cachedProfile) {
          revalidarPerfilEmSegundoPlano(session.user, cachedProfile);
        } else {
          fetchUserProfile(session.user);
        }
      } else if (error) {
        // Falha ao renovar o token (rede instável): manter o perfil em cache, nunca deslogar
        console.warn('⚠️ [Auth] Não foi possível verificar a sessão, mantendo perfil em cache:', error);
        setLoading(false);
      } else {
        // Sem sessão válida o cache não pode sustentar o login: as consultas rodariam
        // como anon e voltariam vazias (ex.: lista de eventos, bloqueada pela RLS)
        console.log('🚫 Nenhuma sessão ativa');
        if (cachedProfile) {
          clearUserProfileCache();
          setUser(null);
        }
        setLoading(false);
      }
    });

    // Escutar mudanças de autenticação. A carga inicial fica com o getSession acima.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state change:', event, session?.user?.email);
      setSession(session);

      if (event === 'SIGNED_IN' && session?.user) {
        // SIGNED_IN também dispara ao recuperar a sessão (ex.: voltar à aba);
        // com perfil em cache do mesmo e-mail, só revalidar sem trocar o papel em falha
        const perfilAtual = loadUserProfileFromCache();
        if (perfilAtual && perfilAtual.email === session.user.email) {
          revalidarPerfilEmSegundoPlano(session.user, perfilAtual);
          return;
        }
        console.log('✅ [Auth] Login detectado, buscando perfil atualizado...');
        await fetchUserProfile(session.user);
      } else if (event === 'SIGNED_OUT') {
        console.log('🚫 Sessão encerrada');
        setUser(null);
        clearUserProfileCache(); // Limpar cache no logout
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  // Revalida o perfil em cache sem bloquear a interface. Em sucesso aplica o papel do
  // banco (inclusive rebaixamento); em falha mantém o cache em vez de adivinhar um papel.
  const revalidarPerfilEmSegundoPlano = async (supabaseUser: SupabaseUser, perfilEmCache: User) => {
    try {
      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', supabaseUser.email)
        .single();

      if (error || !profile) {
        console.warn('⚠️ [Auth] Revalidação do perfil falhou, mantendo cache:', error);
        return;
      }

      const atualizado = await resolverVinculoParticipante({
        id: profile.id,
        nome: profile.nome || supabaseUser.email?.split('@')[0] || 'Usuário',
        email: profile.email,
        papel: (profile.papel as UserRole) || perfilEmCache.papel,
        participantId: profile.participant_id,
        eventId: profile.event_id,
        createdAt: profile.created_at,
        updatedAt: profile.updated_at
      });

      if (atualizado.papel !== perfilEmCache.papel) {
        console.log('🔁 [Auth] Papel atualizado pelo banco:', perfilEmCache.papel, '->', atualizado.papel);
      }
      setUser(atualizado);
      saveUserProfileToCache(atualizado);
    } catch (error) {
      console.warn('⚠️ [Auth] Erro ao revalidar perfil, mantendo cache:', error);
    }
  };

  const fetchUserProfile = async (supabaseUser: SupabaseUser) => {
    try {
      console.log('🔍 Buscando perfil para:', supabaseUser.email, 'ID:', supabaseUser.id);
      
      // Adicionar timeout específico para a consulta
      const queryPromise = supabase
        .from('users')
        .select('*')
        .eq('email', supabaseUser.email)
        .single();
      
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Query timeout after 3 seconds')), 3000)
      );
      
      const { data: profile, error } = await Promise.race([queryPromise, timeoutPromise]);

      console.log('📊 Resposta da consulta:', { profile, error });

      if (error) {
        console.error('❌ Erro ao buscar perfil:', error);
        console.error('❌ Código do erro:', error.code);
        console.error('❌ Mensagem do erro:', error.message);
        console.error('❌ Detalhes do erro:', error.details);
        
        // O papel vem somente do banco. Sem perfil cadastrado (ou inativo, que a RLS
        // esconde) a conta não tem acesso; nunca deduzir papel a partir do e-mail.
        if (error.code === 'PGRST116') {
          console.warn('🚫 [SUPABASE-AUTH] Conta sem perfil ativo em public.users, encerrando sessão');
          await supabase.auth.signOut();
        }
        setUser(null);
        clearUserProfileCache();

      } else if (profile) {
        console.log('📊 Dados do perfil encontrado:', profile);
        console.log('📊 Colunas disponíveis:', Object.keys(profile));
        
        console.log('✅ [SUPABASE-AUTH] Perfil encontrado no banco:', profile);
        
        const user = await resolverVinculoParticipante({
          id: profile.id,
          nome: profile.nome || supabaseUser.email?.split('@')[0] || 'Usuário',
          email: profile.email,
          papel: profile.papel as UserRole || 'REVENDA', // 🚨 FASE 2: Melhorar esta lógica
          participantId: profile.participant_id,
          eventId: profile.event_id,
          createdAt: profile.created_at,
          updatedAt: profile.updated_at
        });
        
        // 🔍 FASE 1: Log detalhado da criação do usuário
        console.log('📊 [SUPABASE-AUTH] Dados do perfil processados:', {
          originalPapel: profile.papel,
          finalPapel: user.papel,
          fallbackUsed: !profile.papel,
          userId: user.id,
          email: user.email
        });
        
        // Alerta crítico se papel foi alterado por fallback
        if (!profile.papel && user.papel === 'REVENDA') {
          console.warn('🚨 [SUPABASE-AUTH] ATENÇÃO: Papel REVENDA aplicado por fallback - pode ser incorreto!', {
            profile,
            user,
            supabaseUserData: supabaseUser
          });
        }
        
        setUser(user);
        saveUserProfileToCache(user); // 💾 Salvar no cache
        console.log('✅ [SUPABASE-AUTH] Perfil carregado com sucesso:', user);
      } else {
        console.warn('⚠️  Nenhum perfil retornado, mas também nenhum erro');
        throw new Error('Nenhum perfil encontrado');
      }
    } catch (error) {
      console.error('❌ [SUPABASE-AUTH] Erro fatal ao buscar perfil:', error);
      console.log('📊 [SUPABASE-AUTH] Stack trace completo:', error);
      
      // Sem perfil confirmado pelo banco não há papel: o usuário volta ao login
      setUser(null);
      clearUserProfileCache();
    } finally {
      console.log('🏁 Finalizando fetchUserProfile, definindo loading = false');
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      return { error };
    } catch (error) {
      console.error('Erro no login:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setUser(null);
      clearUserProfileCache(); // 🧹 Limpar cache no logout
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      setLoading(false);
    }
  };

  // Métodos de compatibilidade com AuthContext
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const result = await signIn(email, password);
      // Retorna true apenas se não houver erro
      return !result.error;
    } catch (error) {
      console.error('Erro no método login de compatibilidade:', error);
      return false;
    }
  };

  const logout = () => {
    signOut();
  };

  const isAuthenticated = !!user;

  const hasPermission = (action: string, resource?: string): boolean => {
    if (!user) return false;
    
    // Usar o sistema RBAC
    const userPermissions = ROLE_PERMISSIONS[user.papel] || [];
    return userPermissions.includes(action);
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
    login,
    logout,
    isAuthenticated,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-pulse-primary mb-4">
              <div className="w-8 h-8 bg-primary rounded-full mx-auto"></div>
            </div>
            <p className="text-muted-foreground">
              {session ? 'Carregando perfil do usuário...' : 'Verificando autenticação...'}
            </p>
            <p className="text-xs text-muted-foreground/60 mt-2">
              {session?.user?.email && `Usuário: ${session.user.email}`}
            </p>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

// Hook para redirecionamento baseado em papel do usuário
export function useRoleRedirect() {
  const { user } = useAuth();
  
  return (targetRole?: UserRole) => {
    if (!user) return '/login';
    
    if (targetRole && user.papel !== targetRole) {
      return getRedirectPath(user.papel as RbacUserRole);
    }
    
    return getRedirectPath(user.papel as RbacUserRole);
  };
}

// HOC para proteger componentes com autenticação e permissões
export function withAuth<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermission?: string
): React.FC<P> {
  const WithAuthComponent: React.FC<P> = (props) => {
    const { user, loading, hasPermission } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      // Se ainda está carregando, aguardar
      if (loading) return;

      // Se não está autenticado, redirecionar para login
      if (!user) {
        console.log('🔒 [withAuth] Usuário não autenticado, redirecionando para login');
        navigate('/login', { replace: true });
        return;
      }

      // Se tem permissão requerida, verificar se o usuário tem a permissão
      if (requiredPermission && !hasPermission(requiredPermission)) {
        console.warn(`🚫 [withAuth] Usuário ${user.email} não tem permissão: ${requiredPermission}`);
        console.warn(`🚫 [withAuth] Papel atual: ${user.papel}`);
        
        // Redirecionar para página apropriada baseada no papel
        const redirectPath = getRedirectPath(user.papel as RbacUserRole);
        navigate(redirectPath, { replace: true });
        return;
      }

      console.log(`✅ [withAuth] Acesso autorizado para ${user.email}`, {
        component: WrappedComponent.name,
        papel: user.papel,
        permissaoRequerida: requiredPermission
      });
    }, [user, loading, navigate, hasPermission]);

    // Mostrar loading enquanto verifica autenticação
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-pulse-primary mb-4">
              <div className="w-8 h-8 bg-primary rounded-full mx-auto"></div>
            </div>
            <p className="text-muted-foreground">Verificando permissões...</p>
          </div>
        </div>
      );
    }

    // Se não está autenticado, não renderizar nada (vai redirecionar)
    if (!user) {
      return null;
    }

    // Se tem permissão requerida mas não tem a permissão, não renderizar
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return null;
    }

    // Renderizar o componente se tudo está OK
    return <WrappedComponent {...props} />;
  };

  // Definir displayName para debug
  WithAuthComponent.displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithAuthComponent;
}

export default SupabaseAuthProvider;